import { useId } from 'react';
import clsx from 'clsx';

import { ChevronLeftIcon, ChevronRightIcon } from '../internal/icons';
import { IconButton } from './IconButton';
import { Select } from './Select';
import styles from './Pagination.module.css';

export interface PaginationProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the page-size control. */
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
  /** Accessible name for the nav landmark. */
  label?: string;
  /** Noun for the range readout: "1–25 of 342 rows". */
  itemLabel?: string;
  compact?: boolean;
  className?: string;
}

const DEFAULT_PAGE_SIZES = [25, 50, 100, 200] as const;

export function Pagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  label = 'Pagination',
  itemLabel = 'rows',
  compact = false,
  className,
}: PaginationProps) {
  const sizeSelectId = useId();
  const totalPages = totalItems === 0 ? 1 : Math.ceil(totalItems / pageSize);
  const clampedPage = Math.min(Math.max(page, 1), totalPages);
  const first = totalItems === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
  const last = Math.min(clampedPage * pageSize, totalItems);

  return (
    <nav aria-label={label} className={clsx(styles.root, compact && styles.compact, className)}>
      {onPageSizeChange !== undefined && (
        <div className={styles.size}>
          <label className={styles.sizeLabel} htmlFor={sizeSelectId}>
            {itemLabel} per page
          </label>
          <Select
            id={sizeSelectId}
            size="sm"
            className={styles.sizeSelect}
            value={String(pageSize)}
            onChange={(event) => onPageSizeChange(Number(event.currentTarget.value))}
            options={pageSizeOptions.map((option) => ({
              value: String(option),
              label: String(option),
            }))}
          />
        </div>
      )}

      {/* Polite live region: the readout is the only confirmation that a page
          change landed for a user who cannot see the table repaint. */}
      <p className={styles.range} aria-live="polite">
        <span className={styles.rangeNumbers}>
          {first}&ndash;{last}
        </span>{' '}
        of {totalItems} {itemLabel}
      </p>

      <div className={styles.pager}>
        <span className={styles.pageLabel}>
          Page <span className={styles.pageNumber}>{clampedPage}</span> of {totalPages}
        </span>
        <IconButton
          label="Previous page"
          icon={<ChevronLeftIcon />}
          size="sm"
          variant="secondary"
          disabled={clampedPage <= 1}
          onClick={() => onPageChange(clampedPage - 1)}
        />
        <IconButton
          label="Next page"
          icon={<ChevronRightIcon />}
          size="sm"
          variant="secondary"
          disabled={clampedPage >= totalPages}
          onClick={() => onPageChange(clampedPage + 1)}
        />
      </div>
    </nav>
  );
}
